// eslint-disable-next-line no-restricted-imports
import * as React from 'react';
import {useEffect, useRef} from 'react';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import {ResizablePanelReact} from 'common/components/resizable-panel';
import {
  Direction,
  PanelName,
  PanelVisibility,
  ResizeToCollapseMode,
  Theme,
} from 'common/components/resizable-panel/types';
import {useCurrentUser} from 'common/contexts/react/current-user-context';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {generateShortcutLabel, getCommandKey, keyEventSwitcher} from 'common/util/keyboard';
import {SelectReact} from 'common/components/select';
import {getProjectId, getReportName} from 'common/report/util';
import {useTranslation} from 'common/i18n/hooks';
import {ArrowType, Placement} from 'common/components/tooltip/types';
import {ModalReact, ModalType, VerticalAlignment} from 'common/components/modal';
import {useDomEvent} from 'common/hooks/useDomEvent';
import {ModalChangeEvent} from 'common/components/modal/types';
import {Visibility} from 'common/components/base/stackable';
import {Permission, ProjectFeatureFlag, ReportName} from 'common/report/constants';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {Directions} from 'common/components/drop-menu/types';
import {ProjectSwitcher} from 'iron/routes/global-sidenav/widgets/project-switcher';
import {CreateProjectModalReact} from 'common/widgets/create-project-modal';
import {ToastPortalReact} from 'common/components/toast-portal';
import {ToastType} from 'common/components/toast/types';
import {InviteUsersModalReact} from 'common/widgets/invite-users-modal';
import {addToast} from 'common/components/toast-area/toast-manager';
import {
  isCanvasEnabled,
  isCohortsInLexiconEnabled,
  isHeatMapsEnabled,
  isSessionRecordingHomeEnabled,
  useTemplateSearch,
} from 'common/report/util/feature-sampling';
import {useProjects} from 'common/contexts/react/projects-context';
import {
  usePersistLastSeenUrlData,
  useRedirectInvalidProject,
  useRedirectInvalidWorkspace,
  useShowFYTModalIfNeeded,
} from 'iron/routes/global-sidenav/hooks';
import {getFilteredOrgs} from 'iron/routes/global-sidenav/utils';
import {useMountEffect} from 'common/hooks/useMountEffect';
import {currentProjectStore} from 'common/report/stores/project-details';
import {viewedReportFlagManager} from 'common/report/queries/app-api/user-flags';
import {useGlobalSidenavController} from 'common/contexts/react/global-sidenav-controller-context';
import {getLocation} from 'common/util/url';
import {ChangeEvent, SelectOpenMode} from 'common/components/select/types';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';
import {useUserDetails} from 'common/contexts/react/user-details-context';
import {UserDetails} from 'common/report/stores/user-details';
import {useTours} from 'common/contexts/react/tours-context';
import {TooltipReact} from 'common/components/tooltip';
import {CountdownPanelCardReact} from 'common/widgets/countdown-panel-card';
import {SidenavReact} from 'common/components/sidenav';
import {react2panel} from 'common/util/react2panel';
import {withStyles} from 'common/util/react';
import {useContentSidenavContext} from 'common/widgets/content-sidenav/context';
import {SvgIconReact} from 'common/components/svg-icon';
import {GlobalSearchReact, GlobalSearchSectionType} from 'iron/routes/global-sidenav/widgets/global-search';
import {SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightReact} from 'common/components/text-highlight';
import {prefersDarkColorScheme} from 'common/util/dom';
import {useThemes} from 'common/contexts/react/themes-context';
import {UpgradePlanButton} from 'apps/settings/src/mixpanel-settings/screens/org/plan-new/upgrade-plan';
import {eligibleForNewPlanBuilderFeature} from 'apps/settings/src/mixpanel-settings/screens/org/plan-new/utils';
import {hasAccessToExperimentsApp} from 'common/util/experiments-feature-gate';

import {ExpansionTrigger, Props as GlobalSidenavControllerProps} from './controller/types';
import {GlobalSidenavProps, MenuType, NavigationLinkItem, NewReportSectionItem, TagTheme, TrackingEvent} from './types';
import {GlobalSidenavFooter} from './footer';
import styles from './index.module.styl';
import css from './index.module.styl?inline';

const ResizablePanelStorageKey = `global-sidenav`;

function CreateNewSelect(props: {userDetails: UserDetails}) {
  const i18n = useTranslation();
  const contentSidenavContext = useContentSidenavContext()!;
  const currentProject = useCurrentProject().get().value;
  const globalSidenavController = useGlobalSidenavController();

  const boardItems: NewReportSectionItem[] = [
    {
      label: i18n.t(`mixology.board`),
      value: ReportName.Dashboards,
      icon: `brand-dashboard`,
      onSelect: () => contentSidenavContext?.createNewContent(),
    },
  ];
  const reportItems: NewReportSectionItem[] = [
    {
      label: i18n.t(`mixology.app.insights`),
      value: ReportName.Insights,
      icon: `brand-insights`,
      href: globalSidenavController.getWorkspaceBasedAppUrl(`insights`),
    },
    {
      label: i18n.t(`mixology.app.funnels`),
      value: ReportName.Funnels,
      icon: `brand-funnels`,
      href: globalSidenavController.getWorkspaceBasedAppUrl(`funnels`),
    },
    {
      label: i18n.t(`mixology.app.flows`),
      value: ReportName.Flows,
      icon: `brand-flows`,
      href: globalSidenavController.getWorkspaceBasedAppUrl(`flows`),
    },
    {
      label: i18n.t(`mixology.app.retention`),
      value: ReportName.Retention,
      icon: `brand-retention`,
      href: globalSidenavController.getWorkspaceBasedAppUrl(`retention`),
    },
  ];

  const extraItems: NewReportSectionItem[] = [];
  const showAddExperiment = hasAccessToExperimentsApp(currentProject);
  if (showAddExperiment) {
    const permissions =
      currentProject?.id !== undefined ? props.userDetails.projects?.[currentProject?.id]?.permissions || [] : [];
    if (permissions.includes(Permission.WriteExperiments) || props.userDetails.isStaff) {
      extraItems.push({
        label: i18n.t(`mixology.experiment`),
        value: ReportName.Experiments,
        icon: `brand-experiments`,
        href: globalSidenavController.getWorkspaceBasedAppUrl(`experiments/new`),
        tag: i18n.t(`common.new`),
        tagTheme: TagTheme.Neutral,
      });
    }
  }

  const showAddFeatureFlag = currentProject?.hasFeatureFlag(ProjectFeatureFlag.FeatureFlagsApp);
  if (showAddFeatureFlag) {
    const permissions =
      currentProject?.id !== undefined ? props.userDetails.projects?.[currentProject?.id]?.permissions || [] : [];
    if (permissions.includes(Permission.WriteFeatureFlags) || props.userDetails.isStaff) {
      extraItems.push({
        label: i18n.t(`mixology.feature-flag`),
        value: ReportName.FeatureFlags,
        icon: `brand-feature-flag`,
        href: globalSidenavController.getWorkspaceBasedAppUrl(`feature-flags/new`),
        tag: i18n.t(`common.beta`),
      });
    }
  }

  const heatMapsFeatureFlagCheck = isHeatMapsEnabled(currentProject);
  if (heatMapsFeatureFlagCheck) {
    const permissions =
      currentProject?.id !== undefined ? props.userDetails.projects?.[currentProject?.id]?.permissions || [] : [];
    if (permissions.includes(Permission.WriteHeatMaps) || props.userDetails.isStaff) {
      extraItems.push({
        label: i18n.t(`mixology.heat-map`),
        value: ReportName.HeatMaps,
        icon: `brand-heat-map`,
        href: globalSidenavController.getWorkspaceBasedAppUrl(`heatmaps`),
      });
    }
  }

  const showAddCanvas = isCanvasEnabled(currentProject || null);
  if (showAddCanvas) {
    const permissions =
      currentProject?.id !== undefined ? props.userDetails.projects?.[currentProject?.id]?.permissions || [] : [];
    if (permissions.includes(Permission.WriteMetricTrees) || props.userDetails.isStaff) {
      boardItems.push({
        label: i18n.t(`mixology.metric-tree`),
        value: ReportName.Canvas,
        icon: `brand-canvas`,
        href: globalSidenavController.getWorkspaceBasedAppUrl(`metric-tree`),
        tag: i18n.t(`common.new`),
        tagTheme: TagTheme.Neutral,
      });
    }
  }

  const sections = extraItems.length
    ? [{items: boardItems}, {items: reportItems}, {items: extraItems}]
    : [{items: boardItems}, {items: reportItems}];

  let selectLabel: string | undefined;
  if (!globalSidenavController.isCollapsed()) {
    selectLabel = i18n.t(`global-sidenav.create-new`);
  }

  return (
    <SelectReact
      // The button's text is shown/hidden depending on if the button is expanded/hidden. The text is a slotted
      // child, which doesn't re-render when changed. However, it's necessary to re-render because layout is
      // different depending on if the button has a label vs. is icon-only. Changing the key to force a
      // re-render.
      key={`create-report-collapsed:${globalSidenavController.isCollapsed()}`}
      className={styles.report}
      label={selectLabel}
      menuWidth={
        !globalSidenavController.isCollapsed()
          ? 0 // menu will be the same width as the select
          : -1 // menu will grow to fit content
      }
      menuHeight={500}
      menuAlignment={globalSidenavController.isCollapsed() ? Directions.Right : Directions.Down}
      iconOnly={globalSidenavController.isCollapsed()}
      icon={{leftIcon: `plus`}}
      neverShowCaret
      experimentalPositioning
      sections={sections}
      openMode={globalSidenavController.isCollapsed() ? SelectOpenMode.Hover : SelectOpenMode.Click}
      onChange={(ev) => {
        globalSidenavController.mixpanelTrack(TrackingEvent.NewReportCreated, {
          'Selected Report Name': (ev as ChangeEvent).detail.item.value,
        });
      }}
      onMenuChange={(ev) => {
        if (ev.detail.isOpen) {
          globalSidenavController.mixpanelTrack(TrackingEvent.NewReportMenuOpened);
        }
      }}
    />
  );
}

const TAG_THEME_MAP = {
  [TagTheme.Default]: TEXT_HIGHLIGHT_THEME.INFORMATION,
  [TagTheme.Neutral]: TEXT_HIGHLIGHT_THEME.GENERIC,
};

function NavigationList(props: {className?: string; onSearchButtonClick: () => void}) {
  const i18n = useTranslation();
  const currentProject = useCurrentProject().get().value;
  const globalSidenavController = useGlobalSidenavController();
  const tours = useTours();

  const navigationItems: NavigationLinkItem[] = (
    [
      ...(!currentProject?.hasFeatureFlag(ProjectFeatureFlag.DisableHome)
        ? [
            {
              label: i18n.t(`mixology.app.home`),
              value: ReportName.Home,
              icon: `home`,
              href: globalSidenavController.getWorkspaceBasedAppUrl(`home`),
            },
          ]
        : []),
      {
        label: i18n.t(`sidenav.discover`),
        value: `discover`,
        icon: `discover`,
        href: globalSidenavController.getWorkspaceBasedAppUrl(`boards#discover`),
      },
      ...(hasAccessToExperimentsApp(currentProject)
        ? [
            {
              label: i18n.t(`sidenav.experiments`),
              value: ReportName.Experiments,
              icon: `experiment`,
              href: globalSidenavController.getWorkspaceBasedAppUrl(`experiments`),
              tag: i18n.t(`common.new`),
              tagTheme: TagTheme.Neutral,
            },
          ]
        : []),
      ...(currentProject?.hasFeatureFlag(ProjectFeatureFlag.FeatureFlagsApp)
        ? [
            {
              label: i18n.t(`sidenav.feature-flags`),
              value: ReportName.FeatureFlags,
              icon: `feature-flag`,
              href: globalSidenavController.getWorkspaceBasedAppUrl(`feature-flags`),
              tag: i18n.t(`common.beta`),
            },
          ]
        : []),
      {
        label: i18n.t(`mixology.app.events`),
        value: ReportName.Events,
        icon: `event`,
        href: globalSidenavController.getWorkspaceBasedAppUrl(`events`),
      },
      {
        label: i18n.t(`mixology.app.users`),
        value: ReportName.Users,
        icon: `team`,
        href: globalSidenavController.getWorkspaceBasedAppUrl(`users`),
      },
      {
        label: i18n.t(`mixology.app.lexicon`),
        value: ReportName.Lexicon,
        icon: `brand-lexicon`,
        href: globalSidenavController.getWorkspaceBasedAppUrl(`lexicon`),
        isSelectedFn: (p) => {
          return p.currentUrl.pathname.startsWith(p.itemUrl.pathname);
        },
      },
      ...(!isCohortsInLexiconEnabled(currentProject)
        ? [
            {
              label: i18n.t(`mixology.app.cohorts`),
              value: ReportName.Cohorts,
              icon: `brand-cohorts`,
              href: globalSidenavController.getWorkspaceBasedAppUrl(`entity-management#~(entityType~'cohorts)`),
              isSelectedFn: (p) => {
                return Boolean(
                  p.currentUrl.pathname === p.itemUrl.pathname && p.currentUrl.hash.includes(`entityType~'cohorts`),
                );
              },
            } satisfies NavigationLinkItem,
          ]
        : []),
      ...(isSessionRecordingHomeEnabled(currentProject)
        ? [
            {
              label: i18n.t(`mixology.app.session_replay`),
              value: ReportName.SessionReplay,
              icon: `replay`,
              href: globalSidenavController.getWorkspaceBasedAppUrl(`session-replay`),
            },
          ]
        : []),
    ] satisfies NavigationLinkItem[]
  ).map((navItem) => ({
    ...navItem,
    ...(!navItem.isSelectedFn && {
      // default is selected logic for nav items
      isSelectedFn: ((p) => {
        return Boolean(p.currentUrl.pathname === p.itemUrl.pathname && p.currentUrl.hash.startsWith(p.itemUrl.hash));
      }) satisfies NavigationLinkItem[`isSelectedFn`],
    }),
  }));

  const currentUrl = getLocation();

  return (
    <div className={clsx(styles.navigationList, props.className)}>
      <ButtonReact
        className={styles.navigationItem}
        icon='search'
        key={`navigation-item-search-collapsed:${globalSidenavController.isCollapsed()}`}
        theme={ButtonTheme.Tertiary}
        type={ButtonType.Info}
        onClick={props.onSearchButtonClick}
        tooltip={
          globalSidenavController.isCollapsed()
            ? {
                text:
                  `${i18n.t(`common.search`)}` +
                  ` ${generateShortcutLabel(`K`, {modifiers: [getCommandKey()], forMarkdown: true})}`,
                placement: Placement.Right,
              }
            : undefined
        }
      >
        {!globalSidenavController.isCollapsed() && (
          <div className={styles.searchLabelContainer}>
            <span>{i18n.t(`common.search`)}</span>
            <span className={styles.searchShortcut}>
              {generateShortcutLabel(`K`, {modifiers: [getCommandKey()], forMarkdown: false})}
            </span>
          </div>
        )}
      </ButtonReact>
      {navigationItems.map((navigationItem) => {
        return (
          <ButtonReact
            key={`navigation-item-${navigationItem.label}-collapsed:${globalSidenavController.isCollapsed()}`}
            className={clsx(styles.navigationItem, {
              [styles.selected]: Boolean(
                navigationItem.isSelectedFn?.({
                  itemUrl: new URL(window.location.origin + navigationItem.href),
                  currentUrl,
                }),
              ),
            })}
            icon={navigationItem.icon}
            href={navigationItem.href}
            theme={ButtonTheme.Tertiary}
            type={ButtonType.Info}
            disabled={navigationItem.isDisabled}
            tooltip={
              navigationItem.tooltip ??
              (globalSidenavController.isCollapsed()
                ? {
                    text: navigationItem.label,
                    placement: Placement.Right,
                  }
                : undefined)
            }
            onClick={() => {
              globalSidenavController.mixpanelTrack(TrackingEvent.AppLinkClicked, {
                'Selected Report Name': navigationItem.value,
              });
            }}
          >
            {!globalSidenavController.isCollapsed() && (
              <div className={styles.navigationItemLabel}>
                {navigationItem.label}
                {navigationItem.tag && (
                  <div className={styles.navigationItemTag}>
                    <TextHighlightReact
                      theme={TAG_THEME_MAP[navigationItem.tagTheme || TagTheme.Default]}
                      size={SIZE.SMALL}
                      text={navigationItem.tag}
                    />
                  </div>
                )}
              </div>
            )}
          </ButtonReact>
        );
      })}
      {tours.isShowingGlobalSidenavTour() && !globalSidenavController.isCollapsed() && (
        <TooltipReact
          text={i18n.t(`tours.global-sidenav.tooltip.your-home`)}
          experimentalPositioning
          forceShow
          placement={Placement.RightCenter}
          arrowType={ArrowType.LineDot}
        />
      )}
    </div>
  );
}

function SearchModal(props: {showSearchModal: boolean; onClose: () => void}) {
  return (
    <ModalReact
      className={styles.searchModal}
      modalType={ModalType.Modal}
      verticalAlignment={VerticalAlignment.Top}
      closeable
      hideCloseButton
      growToContent
      open={props.showSearchModal}
      onChange={(ev) => {
        const evt = ev as ModalChangeEvent;
        if (evt.detail.state === Visibility.Closed) {
          props.onClose();
        }
      }}
    >
      <div className={styles.searchModalContent}>
        <GlobalSearchReact
          onCompleteSearch={props.onClose}
          showingSections={[
            GlobalSearchSectionType.BrowseAll,
            GlobalSearchSectionType.Creator,
            GlobalSearchSectionType.Entity,
            ...(useTemplateSearch() ? [GlobalSearchSectionType.BoardTemplate] : []),
          ]}
        />
      </div>
    </ModalReact>
  );
}

export function GlobalSidenavContent(props: GlobalSidenavProps) {
  const currentUser = useCurrentUser().get().value;
  const currentProject = useCurrentProject().get().value;
  const projectsResource = useProjects();
  const contentSidenavContext = useContentSidenavContext();
  const globalSidenavController = useGlobalSidenavController();
  const i18n = useTranslation();
  const userDetailsStore = useUserDetails();
  const tours = useTours();
  const themes = useThemes();

  const [openMenus, setOpenMenus] = React.useState<Record<MenuType, boolean>>({
    [MenuType.CreateProjectModal]: false,
  });

  const [errorAlertMsg, setErrorAlertMsg] = React.useState<string>();

  const setMenuOpen = (menu: MenuType, isOpen: boolean) => {
    setOpenMenus({...openMenus, [menu]: isOpen});
  };

  const [showSearchModal, setShowSearchModal] = React.useState(false);
  const [isExpandTargetHovered, setIsExpandTargetHovered] = React.useState(false);

  const isSidenavCollapsed = globalSidenavController.isCollapsed();

  const orgIdForCreateProject = (): number | undefined => {
    const orgsWithCreatePermission = getFilteredOrgs(currentUser?.organizations ?? {}, {
      permissions: [Permission.CreateProject],
    });
    return orgsWithCreatePermission[0]?.id;
  };

  const handleCreateProject = (ev: CustomEvent) => {
    try {
      // comes from CreateProjectModalEl
      if (ev?.detail?.error) {
        throw new Error(ev.detail.error);
      }
      const newProjectId = ev?.detail?.response?.id;
      window.location.assign(`/report/${newProjectId}/`);
    } catch (error) {
      setErrorAlertMsg(`Failed to create new project. Error: ${error.toString()}`);
    }
  };

  const toggleSearchModal = ({keyboardShortcut}: {keyboardShortcut?: string} = {}) => {
    const newShowSearchModal = !showSearchModal;
    if (newShowSearchModal) {
      globalSidenavController.mixpanelTrack(TrackingEvent.SearchModalOpened, {
        'Keyboard Shortcut': keyboardShortcut,
      });
    }
    setShowSearchModal(newShowSearchModal);
  };

  const contentSidenavProps = contentSidenavContext?.getSidenavProps();

  const dismissTour = () => {
    if (tours.isShowingGlobalSidenavTour()) {
      tours.dismissTour({tour: `global-sidenav`, persist: true});
    }
  };

  const handleKeydownToggleSearchModal = React.useCallback(
    (ev: KeyboardEvent) => {
      const keyboardShortcut = `${getCommandKey()} + KeyK`;
      return keyEventSwitcher(
        {
          [keyboardShortcut]: () => {
            if (!currentProject) {
              return;
            }

            dismissTour();
            toggleSearchModal({keyboardShortcut});
          },
        },
        {stopPropagation: true, usableInInputs: true},
      )(ev);
    },
    [currentProject],
  );
  useDomEvent(document, `keydown`, handleKeydownToggleSearchModal as EventListener);

  const handleKeydownToggleExpansion = React.useCallback((ev: KeyboardEvent) => {
    return keyEventSwitcher(
      {
        [`Backquote`]: () => {
          globalSidenavController.updateExpansionMode({
            isCollapsed: !globalSidenavController.isCollapsed(),
            expansionSource: ExpansionTrigger.KeyboardShortcut,
          });
        },
      },
      {stopPropagation: true, usableInInputs: false},
    )(ev);
  }, []);
  useDomEvent(document, `keydown`, handleKeydownToggleExpansion as EventListener);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const hoursAgoJoined = moment.utc().diff(moment.utc(currentUser.dateJoinedIso), `hours`, true);
    if (hoursAgoJoined < 72) {
      // prevent tour from showing for newly joined users.
      tours.persistTourDismissal(`global-sidenav`);
    } else if (!globalSidenavController.isCollapsed()) {
      tours.openTour(`global-sidenav`);
    }
  }, [currentUser, tours, globalSidenavController]);

  useEffect(() => {
    document.addEventListener(`mousedown`, dismissTour);
    return () => {
      document.removeEventListener(`mousedown`, dismissTour);
    };
  }, []);

  useMountEffect(() => {
    globalSidenavController.mixpanelTrack(TrackingEvent.Mounted, {
      'Localization Language Code': i18n.language,
      'Color Scheme Setting': themes.getColorSchemeSetting(),
      'Prefers Dark Color Scheme': prefersDarkColorScheme(),
    });
  });

  useMountEffect(() => {
    userDetailsStore.fetch();
  });

  useMountEffect(() => {
    const projectId = parseInt(getProjectId(), 10);
    // projectId can be empty if user is on a page that's outside of a project (e.g. admin org settings)
    // it could also  be empty if user does not have access to any project
    //   - see https://mixpanel.slack.com/archives/C014L0QMH1T/p1592860852025000
    if (projectId) {
      // TODO remove this once we're sure no other apps/components are relying on
      // this component to initiate this fetch for them...
      currentProjectStore.fetch();
    }
  });

  const reportName = getReportName();
  useEffect(() => {
    const reportsWithSelectedDashboardNavItemSet = new Set([
      ReportName.Dashboards,
      ReportName.Insights,
      ReportName.Funnels,
      ReportName.Retention,
      ReportName.Flows,
    ]);
    if (contentSidenavContext && !reportsWithSelectedDashboardNavItemSet.has(reportName)) {
      contentSidenavContext.setSidenavState({
        selectedNavItem: undefined,
      });
    }
  }, [reportName]);

  useMountEffect(() => {
    viewedReportFlagManager.increment();
  });

  useShowFYTModalIfNeeded({currentProject, currentUser});

  useRedirectInvalidProject({
    currentUser,
    currentProject,
    projectsResource,
    location: props.location,
  });

  useRedirectInvalidWorkspace({
    currentUser,
    currentProject,
    location: props.location,
  });

  usePersistLastSeenUrlData({currentProject, location: props.location});

  const toggleExpansionShortcutLabel = generateShortcutLabel(`\``, {forMarkdown: true});

  return (
    <ResizablePanelReact
      className={clsx(styles.globalSidenav, props.className)}
      storageKey={ResizablePanelStorageKey}
      minAdjacentSize={100}
      maxSize={450}
      minSize={250}
      panelName={PanelName.SideNav}
      defaultAlignment={Direction.Left}
      defaultRatio={0.15}
      defaultSize={250}
      disableResponsiveResize
      resizeToCollapseMode={ResizeToCollapseMode.Always}
      theme={Theme.GlobalSidenav}
      visibility={globalSidenavController.isCollapsed() ? PanelVisibility.Collapsed : PanelVisibility.Expanded}
      onPanelResize={(ev) => {
        const isCollapsed = ev.detail.isCollapsed;
        if (isCollapsed !== globalSidenavController.isCollapsed()) {
          globalSidenavController.updateExpansionMode({isCollapsed, expansionSource: ev.detail.togglePanelSource});
        }
      }}
    >
      <div
        className={clsx(styles.sidenavContent, {
          [styles.expandTargetHovered]: isSidenavCollapsed && isExpandTargetHovered,
        })}
      >
        <div className={clsx(styles.projectSwitcher, {[styles.collapsedSidenav]: isSidenavCollapsed})}>
          <ProjectSwitcher
            isCollapsedMode={isSidenavCollapsed}
            onClickCreateProject={() => {
              if (!isEmpty(currentUser?.organizations)) {
                setMenuOpen(MenuType.CreateProjectModal, true);
              }
            }}
            onMenuChange={(ev) => {
              if (ev.detail.isOpen) {
                globalSidenavController.mixpanelTrack(TrackingEvent.ProjectSwitcherOpened);
              }
            }}
          />
        </div>

        {currentProject && (
          <>
            <div className={clsx(styles.createContainer, {[styles.collapsedSidenav]: isSidenavCollapsed})}>
              <CreateNewSelect userDetails={userDetailsStore.value} />
              {tours.isShowingGlobalSidenavTour() && !globalSidenavController.isCollapsed() && (
                <TooltipReact
                  text={i18n.t(`tours.global-sidenav.tooltip.new-report`)}
                  experimentalPositioning
                  forceShow
                  placement={Placement.RightCenter}
                  arrowType={ArrowType.LineDot}
                />
              )}
            </div>

            <NavigationList
              className={clsx({[styles.collapsedSidenav]: isSidenavCollapsed})}
              onSearchButtonClick={toggleSearchModal}
            />

            {contentSidenavProps && (
              <div className={styles.boardsListContainer}>
                {!globalSidenavController.isCollapsed() && (
                  <>
                    <SidenavReact {...contentSidenavProps} className={styles.boardsList} />
                    {tours.isShowingGlobalSidenavTour() && (
                      <TooltipReact
                        text={i18n.t(`tours.global-sidenav.tooltip.your-work`)}
                        experimentalPositioning
                        forceShow
                        placement={Placement.Right}
                        arrowType={ArrowType.LineDot}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}

        <div
          className={styles.emptyAreaContainer}
          onMouseEnter={() => setIsExpandTargetHovered(true)}
          onMouseLeave={() => setIsExpandTargetHovered(false)}
          onClick={() =>
            globalSidenavController.updateExpansionMode({
              isCollapsed: false,
              expansionSource: ExpansionTrigger.CollapsedEmptyArea,
            })
          }
        />

        {currentProject && !isSidenavCollapsed && <CountdownPanelCardReact />}

        {isSidenavCollapsed ? (
          <>
            <div className={styles.collapsedSettingsContainer}>
              <GlobalSidenavFooter location={props.location} />
            </div>
            <div
              className={styles.expandButtonContainer}
              onMouseEnter={() => setIsExpandTargetHovered(true)}
              onMouseLeave={() => setIsExpandTargetHovered(false)}
              onClick={() =>
                globalSidenavController.updateExpansionMode({
                  isCollapsed: false,
                  expansionSource: ExpansionTrigger.FooterButton,
                })
              }
            >
              <SvgIconReact
                className={clsx(styles.expandIcon, {[styles.expandTargetHovered]: isExpandTargetHovered})}
                icon={`push-right`}
              />
              <TooltipReact
                // HACK: fixes the tooltip not disappearing when isExpandTargetHovered changes to false
                key={isExpandTargetHovered.toString()}
                text={`${i18n.t(`global-sidenav.show-side-nav`)} ${toggleExpansionShortcutLabel}`}
                placement={Placement.RightEnd}
                alwaysShow={isExpandTargetHovered}
              />
            </div>
          </>
        ) : (
          <div className={styles.expandedFooterContainer}>
            {currentProject?.isFreeFeatureGateTier() && (
              <UpgradePlanButton
                source={`Global Sidenav`}
                modifyPlan={() =>
                  (location.href = `/settings/org/${currentProject.organizationId}/plan?show-modal=plan-builder`)
                }
                hasInProductPlanBuilderOrgFF={eligibleForNewPlanBuilderFeature(
                  currentProject.organizationId.toString(),
                )}
                className={styles.upgradeButton}
              />
            )}
            <div className={styles.actions}>
              <GlobalSidenavFooter location={props.location} />
              <ButtonReact
                theme={ButtonTheme.Tertiary}
                notFocusable
                icon={`push-left`}
                tooltip={{
                  text: `${i18n.t(`global-sidenav.collapse-side-nav`)} ${toggleExpansionShortcutLabel}`,
                  placement: Placement.RightEnd,
                }}
                onClick={() =>
                  globalSidenavController.updateExpansionMode({
                    isCollapsed: true,
                    expansionSource: ExpansionTrigger.FooterButton,
                  })
                }
              />
              {tours.isShowingGlobalSidenavTour() && (
                <TooltipReact
                  text={i18n.t(`tours.global-sidenav.tooltip.settings-and-more`)}
                  forceShow
                  placement={Placement.RightEnd}
                  arrowType={ArrowType.LineDot}
                />
              )}
            </div>
          </div>
        )}

        <div>
          {currentProject && (
            <SearchModal showSearchModal={showSearchModal} onClose={() => setShowSearchModal(false)} />
          )}

          {openMenus[MenuType.CreateProjectModal] && (
            <CreateProjectModalReact
              open
              orgs={Object.values(currentUser?.organizations ?? {})}
              organizationId={orgIdForCreateProject()}
              onCancel={() => setMenuOpen(MenuType.CreateProjectModal, false)}
              onCreate={handleCreateProject}
            />
          )}
          {globalSidenavController.state.isInviteUserModalOpen && (
            <InviteUsersModalReact
              open
              orgs={globalSidenavController.getInviteUserOrgs()}
              onSave={() => globalSidenavController.closeInviteModal()}
              onCancel={() => globalSidenavController.closeInviteModal()}
              onNotify={(ev: CustomEvent<TODO>) => addToast({message: ev.detail.message})}
              onError={(ev: CustomEvent<TODO>) => {
                const {error, message} = ev.detail;
                setErrorAlertMsg(message || error.message);
              }}
            />
          )}
          {/* special case for project error alert */}
          {errorAlertMsg && (
            <ToastPortalReact
              toast={{
                isStatic: true,
                type: ToastType.ERROR,
                message: errorAlertMsg,
              }}
            />
          )}
        </div>
      </div>
    </ResizablePanelReact>
  );
}

/**
 * NOTE: the withStyles here and on child component are needed because GlobalSidenav is sometimes rendered within
 * shadow dom panel component. Without the withStyles, GlobalSidenav styles would get added to page root which
 * aren't accessible within the shadow dom.
 */
export const GlobalSidenav = withStyles((props: GlobalSidenavProps) => {
  const currentUser = useCurrentUser().get().value;
  const currentProjectResource = useCurrentProject();
  const currentProject = currentProjectResource.get().value;
  const globalSidenavController = useGlobalSidenavController();
  const localStorageManager = useLocalStorageManager();

  const initializedController = useRef(false);
  const controllerProps: GlobalSidenavControllerProps = {
    currentProject,
    currentUser,
    localStorageManager,
    location: props.location,
  };
  useEffect(() => {
    globalSidenavController.updateProps(controllerProps);
    initializedController.current = true;
  }, Object.values(controllerProps));

  if (!initializedController.current) {
    return null;
  }

  return <GlobalSidenavContent {...props} />;
}, css);

export const GlobalSidenavEl = react2panel(`mp-global-sidenav`, GlobalSidenav);
